import type { FC} from "react";
import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { observer } from "mobx-react";
import { Button } from "@web/src/app/components/button";
import { useStores } from "@web/src/hooks/useStore";
import { PiGiftFill, PiCopy, PiCheckCircle } from "react-icons/pi";
import classNames from "classnames";
import { REFERRAL_LIFETIME_REWARD_COUNT } from "@common/types/referral";

interface ReferralsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const REFERRAL_URL_PARAM = "referral_code";

const ReferralsModal: FC<ReferralsModalProps> = observer(({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const { userStore } = useStores();
  const { referralInfo, isLoadingReferralInfo } = userStore;

  useEffect(() => {
    if (isOpen) {
      userStore.fetchReferralInfo();
    }
  }, [isOpen, userStore]);

  // Get the referral URL from the referral code
  const getReferralUrl = () => {
    if (!referralInfo?.referralCode || typeof window === "undefined") return "";
    const url = new URL("/login", window.location.origin);
    url.searchParams.set(REFERRAL_URL_PARAM, referralInfo.referralCode);
    return url.toString();
  };

  const copyToClipboard = async () => {
    const referralUrl = getReferralUrl();
    if (!referralUrl) return;

    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const hasLifetimeReward = referralInfo?.referralCount && referralInfo.referralCount >= REFERRAL_LIFETIME_REWARD_COUNT;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content max-w-md">
          <Dialog.Title className="text-2xl font-bold flex items-center">🎁 Refer Friends, Get Pro</Dialog.Title>

          <div className="mt-4">
            {isLoadingReferralInfo ? (
              <div className="py-4 text-glyph-scd">Loading referral information...</div>
            ) : !referralInfo ? (
              <div className="py-4 text-glyph-scd">Unable to load referral information. Please try again later.</div>
            ) : (
              <div className="flex flex-col">
                <div className="">
                  <p className="text-sm mb-2 text-glyph-scd">
                    Share To-day with friends and get rewarded! If they sign up, you&apos;ll get 1 year of Pro for free.
                  </p>
                  <p className="text-sm font-semibold text-glyph-prm">Get 3 friends to join and earn Pro for life!</p>
                </div>

                <div className="bg-surface-scd p-4 rounded-lg my-4">
                  <div className="flex items-center mb-1">
                    <span className="text-sm mr-2 text-glyph-scd">Successful referrals:</span>
                    <span className="font-bold text-glyph-prm">{referralInfo.referralCount}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm mr-2 text-glyph-scd">Lifetime reward:</span>
                    <span
                      className={classNames("font-semibold", {
                        "text-yellow-2": hasLifetimeReward,
                      })}
                    >
                      {hasLifetimeReward ? "Earned! 🎉" : "Not yet"}
                    </span>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold mb-2 text-glyph-prm">Your referral link</h4>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={getReferralUrl()}
                      readOnly
                      className="flex-1 py-2 px-3 border border-surface-scd rounded-l-lg bg-surface-prm text-glyph-prm text-sm focus:outline-none"
                    />
                    <Button
                      onClick={copyToClipboard}
                      variant="solid"
                      color="primary"
                      className="rounded-l-none"
                      leadingIcon={copied ? <PiCheckCircle size={18} /> : <PiCopy size={18} />}
                    >
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                </div>

                <div className="text-xs text-glyph-trt">
                  <p>
                    Note: Referral rewards are granted automatically when someone signs up using your referral link, but
                    you&apos;ll have to refresh your page.
                  </p>
                </div>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

export default ReferralsModal;
